import type { Channel } from '../link/channel';
import type { Command, MoveCommand, SafetyState } from '../protocol';

export type StopMode = 'command' | 'smooth_stop' | 'full_stop' | 'emergency';

export interface SafetyOutput {
  /** Целевая продольная тяга, -1..1 */
  v: number;
  /** Целевой поворот, -1..1 */
  w: number;
  mode: StopMode;
}

export interface SafetyEvent {
  t: number;
  kind: 'state' | 'stale_command' | 'reorder_drop' | 'queue_flush' | 'estop';
  from?: SafetyState;
  to?: SafetyState;
  detail?: string;
}

/**
 * Машина состояний безопасности робота (п. 10 ТЗ). Живёт на «борту»:
 * видит только те команды, что реально доехали по каналу, и своё время.
 */
export class SafetyController {
  state: SafetyState = 'READY';
  /** На старте связь считается живой: робот только что включился рядом с оператором */
  private lastAcceptedAt = 0;
  private lastSeq = -1;
  private last: MoveCommand | null = null;
  private estop = false;
  private needsRecovery = false;
  private events: SafetyEvent[] = [];

  /** Счётчики для критериев приёмки (п. 15.3) */
  staleDropped = 0;
  reorderDropped = 0;

  constructor(
    private ttlMs: number,
    private linkLossMs: number,
  ) {}

  takeEvents(): SafetyEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  private setState(to: SafetyState, now: number, detail?: string): void {
    if (this.state === to) return;
    this.events.push({ t: now, kind: 'state', from: this.state, to, detail });
    this.state = to;
  }

  update(
    now: number,
    incoming: Command[],
    channel: Channel<Command>,
    speedMs: number,
  ): SafetyOutput {
    for (const cmd of incoming) {
      if (cmd.type === 'discrete') {
        if (cmd.action === 'estop') {
          this.estop = true;
          this.events.push({ t: now, kind: 'estop', detail: 'нажат экстренный стоп' });
        } else if (cmd.action === 'estop_release') {
          // Выход из EMERGENCY_STOP — только явным подтверждением оператора (правило 5)
          this.estop = false;
          this.needsRecovery = true;
          this.last = null;
        }
        continue;
      }

      // Правило 1: команда старше TTL не исполняется никогда, даже если доехала
      if (now - cmd.ts > this.ttlMs) {
        this.staleDropped++;
        this.events.push({ t: now, kind: 'stale_command', detail: `возраст ${Math.round(now - cmd.ts)} мс` });
        continue;
      }
      // Переупорядочивание: пакет старее уже принятого — игнорируем
      if (cmd.seq <= this.lastSeq) {
        this.reorderDropped++;
        this.events.push({ t: now, kind: 'reorder_drop', detail: `seq ${cmd.seq} <= ${this.lastSeq}` });
        continue;
      }

      // Связь вернулась после обрыва: чистим всё, что ещё летит по каналу (правило 1),
      // и требуем от оператора нулевую команду (правило 3)
      if (this.state === 'LINK_LOST') {
        const flushed = channel.flush();
        this.events.push({ t: now, kind: 'queue_flush', detail: `сброшено ${flushed} пакетов` });
        this.needsRecovery = true;
      }

      this.lastSeq = cmd.seq;
      this.lastAcceptedAt = now;
      this.last = cmd;
    }

    const silence = now - this.lastAcceptedAt;

    if (this.estop) {
      this.setState('EMERGENCY_STOP', now, 'кнопка оператора');
      return { v: 0, w: 0, mode: 'emergency' };
    }

    if (silence > this.linkLossMs) {
      this.setState('LINK_LOST', now, `нет команд ${Math.round(silence)} мс`);
      this.needsRecovery = true;
      this.last = null;
      return { v: 0, w: 0, mode: 'full_stop' };
    }

    if (this.needsRecovery) {
      this.setState('RECOVERING', now, 'ожидание нулевой команды от оператора');
      const zero = !this.last || (Math.abs(this.last.move[1]) < 0.02 && Math.abs(this.last.yaw) < 0.02);
      if (zero && silence <= this.ttlMs) {
        this.needsRecovery = false;
        this.setState('READY', now, 'оператор отпустил управление');
      }
      return { v: 0, w: 0, mode: 'full_stop' };
    }

    if (silence > this.ttlMs) {
      // Правило 2: нет свежих команд — плавный стоп, последняя команда больше не действует
      this.setState('COMMAND_TIMEOUT', now, `нет свежих команд ${Math.round(silence)} мс`);
      return { v: 0, w: 0, mode: 'smooth_stop' };
    }

    const cmd = this.last;
    const v = cmd ? cmd.move[1] : 0;
    const w = cmd ? cmd.yaw : 0;

    if (silence > this.ttlMs * 0.6) {
      // Команды ещё свежие, но канал на грани — предупреждаем оператора заранее
      this.setState('DEGRADED_LINK', now, `задержка команд ${Math.round(silence)} мс`);
    } else if (Math.abs(speedMs) > 0.05) {
      this.setState('MOVING', now);
    } else {
      this.setState('READY', now);
    }

    return { v, w, mode: 'command' };
  }

  /** Возраст последней принятой роботом команды, мс */
  commandAgeMs(now: number): number {
    return now - this.lastAcceptedAt;
  }

  get emergencyActive(): boolean {
    return this.estop;
  }

  /** seq последней исполненной команды — уходит в телеметрию для замера RTT */
  get lastAcceptedSeq(): number {
    return this.lastSeq;
  }
}
